import type { Building } from "building-types/building/buildingTypes";
import type { HalfEdgeToFoundationPath } from "building-types/foundations/foundationTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { RWTable, Table } from "common/data/table";
import { resolveOptionsInFormula } from "doc/formula/evalFormulas";
import {
  generateFoundationHorizontalAssembliesPathsFromFoundations,
  generateFoundationHorizontalAssembliesPathsFromFloors,
} from "../generationTools/generateFoundationHorizontalAssembliesPathTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  settings,
  datumAbstractions,
  optionChoices,
  entityDisabledByGroup,
  planarGraphs,
  mirror,
  mirrorTranslationX,
  floorBoundaries,
  horizontalAssemblyFoundations: foundations,
  horizontalAssemblyFoundationSystems: foundationSystems,
  horizontalAssemblyFloorSystems: floorSystems,
  horizontalAssemblyFloors: floors,
  doc: { optionSets, instanceOptionSets, foundationComponents, foundationAssemblies },
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(
    { foundations, foundationSystems, floors, floorSystems },
    definitions,
    optionChoices,
  );
  return {
    foundationSettings: settings.foundation,
    foundationComponents,
    datumAbstractions,
    foundationAssemblies,
    foundations,
    foundationSystems,
    floorSystems,
    floors,
    floorBoundaries,
    optionChoices: resolvedOptionChoices,
    entityDisabledByGroup,
    planarGraphs,
    mirror,
    mirrorTranslationX,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  foundationSettings: "shallow",
  optionChoices: "shallow",
  floors: "shallow",
  floorSystems: "shallow",
  foundations: "shallow",
  foundationSystems: "shallow",
});

function generate(input: ReturnType<typeof select>): {
  foundationHorizontalAssembliesPaths: Table<HalfEdgeToFoundationPath>;
  issues: BuildingGenerationIssue[];
} {
  const issues: BuildingGenerationIssue[] = [];

  const foundationHorizontalAssembliesPaths = generateFoundationHorizontalAssembliesPathsInput({
    ...input,
    issues,
  });

  return { foundationHorizontalAssembliesPaths: foundationHorizontalAssembliesPaths, issues };
}

function generateFoundationHorizontalAssembliesPathsInput(
  input: ReturnType<typeof select> & { issues: BuildingGenerationIssue[] },
): RWTable<HalfEdgeToFoundationPath> {
  const foundationPathsFromFoundations = generateFoundationHorizontalAssembliesPathsFromFoundations(input);
  const foundationPathsFromFloors = generateFoundationHorizontalAssembliesPathsFromFloors(input);

  return {
    ...foundationPathsFromFloors,
    ...foundationPathsFromFoundations,
  };
}

export default memo(select, generate, compare);
